/**
 * Bố cục 2D cho các DIM trên màn hình (chỉ để HIỂN THỊ, không đổi dữ liệu lưu):
 *  - xếp tầng: các đường DIM song song, chồng nhau thì đẩy dần ra xa vật
 *  - chống đè nhãn: nhãn đè nhau thì dịch sang chỗ trống gần nhất
 *  - ẩn nhãn quá nhỏ: đoạn DIM quá ngắn thì không hiện chữ
 *
 * Mọi hàm là hàm thuần trên dữ liệu hình học đã chiếu (xem projectDimension).
 */

/** Khoảng cách tối thiểu (px) giữa hai đường DIM song song cùng tầng. */
export const TIER_GAP_PX = 18;
/** Góc lệch tối đa (rad) để coi hai đường là song song (~8°). */
const PARALLEL_SIN = Math.sin((8 * Math.PI) / 180);
/** Đoạn DIM ngắn hơn ngưỡng này (px) thì ẩn nhãn. */
export const MIN_LABEL_LENGTH_PX = 16;
/** Tỉ lệ chồng nhau tối thiểu (theo đoạn ngắn hơn) để coi là cùng tầng. */
const MIN_OVERLAP_RATIO = 0.2;

const unit = (dx, dy) => {
  const len = Math.hypot(dx, dy) || 1;
  return { x: dx / len, y: dy / len };
};

/**
 * Xếp tầng các đường DIM song song chồng nhau.
 *
 * @param {{id: string, t1: {x,y}, t2: {x,y}, s1: {x,y}, s2: {x,y}}[]} geos
 *   t = đầu mút đường DIM, s = điểm neo trên vật (đường dóng đi từ s tới t)
 * @returns {Map<string, number>} id -> độ đẩy thêm (px) theo hướng đường dóng
 *   (ra xa vật). DIM không cần đẩy thì không có trong Map.
 */
export const tierDimensionLines = geos => {
  const shifts = new Map();
  // Gần vật trước: DIM xa hơn bị đẩy ra ngoài DIM gần hơn
  const items = geos
    .map(g => {
      const dir = unit(g.t2.x - g.t1.x, g.t2.y - g.t1.y);
      const out = unit(
        (g.t1.x - g.s1.x + (g.t2.x - g.s2.x)) / 2,
        (g.t1.y - g.s1.y + (g.t2.y - g.s2.y)) / 2
      );
      const dist = Math.hypot(g.t1.x - g.s1.x, g.t1.y - g.s1.y);
      return { g, dir, out, dist, shift: 0 };
    })
    .sort((a, b) => a.dist - b.dist);

  const along = (p, dir) => p.x * dir.x + p.y * dir.y;
  const placed = [];
  items.forEach(cur => {
    // Lặp vì đẩy ra có thể lại va vào một tầng khác
    for (let iter = 0; iter < 6; iter += 1) {
      let moved = false;
      for (const other of placed) {
        const cross = Math.abs(
          cur.dir.x * other.dir.y - cur.dir.y * other.dir.x
        );
        if (cross > PARALLEL_SIN) continue;
        // Vị trí hiện tại của hai đường (đã tính phần đẩy)
        const a1 = shifted(cur.g.t1, cur.out, cur.shift);
        const a2 = shifted(cur.g.t2, cur.out, cur.shift);
        const b1 = shifted(other.g.t1, other.out, other.shift);
        const b2 = shifted(other.g.t2, other.out, other.shift);
        // Khoảng cách vuông góc giữa hai đường
        const normal = { x: -other.dir.y, y: other.dir.x };
        const midA = { x: (a1.x + a2.x) / 2, y: (a1.y + a2.y) / 2 };
        const midB = { x: (b1.x + b2.x) / 2, y: (b1.y + b2.y) / 2 };
        const gap = Math.abs(
          (midA.x - midB.x) * normal.x + (midA.y - midB.y) * normal.y
        );
        if (gap >= TIER_GAP_PX) continue;
        // Chồng nhau theo phương của đường?
        const [aMin, aMax] = range(a1, a2, other.dir, along);
        const [bMin, bMax] = range(b1, b2, other.dir, along);
        const overlap = Math.min(aMax, bMax) - Math.max(aMin, bMin);
        const shorter = Math.min(aMax - aMin, bMax - bMin);
        if (overlap <= 0 || overlap < shorter * MIN_OVERLAP_RATIO) continue;
        // Đẩy ra xa vật cho đủ khoảng cách
        cur.shift += TIER_GAP_PX - gap + 2;
        moved = true;
      }
      if (!moved) break;
    }
    if (cur.shift > 0) shifts.set(cur.g.id, cur.shift);
    placed.push(cur);
  });
  return shifts;
};

const shifted = (p, dir, amount) => ({
  x: p.x + dir.x * amount,
  y: p.y + dir.y * amount,
});

const range = (p, q, dir, along) => {
  const a = along(p, dir);
  const b = along(q, dir);
  return [Math.min(a, b), Math.max(a, b)];
};

/**
 * Hộp bao (theo trục màn hình) của một nhãn xoay: tâm (x, y), kích thước w × h
 * trước khi xoay, góc xoay `angle` (rad).
 */
export const labelRect = ({ x, y, w, h, angle = 0 }) => {
  const c = Math.abs(Math.cos(angle));
  const s = Math.abs(Math.sin(angle));
  const hw = (w * c + h * s) / 2;
  const hh = (w * s + h * c) / 2;
  return { minX: x - hw, maxX: x + hw, minY: y - hh, maxY: y + hh };
};

const rectsOverlap = (a, b, pad = 0) =>
  a.minX < b.maxX + pad &&
  a.maxX + pad > b.minX &&
  a.minY < b.maxY + pad &&
  a.maxY + pad > b.minY;

/**
 * Chống đè nhãn: dịch các nhãn đè nhau sang chỗ trống gần nhất.
 *
 * @param {{id: string, x: number, y: number, w: number, h: number,
 *          angle?: number, priority?: number}[]} labels
 *   priority nhỏ hơn = được giữ nguyên vị trí trước (vd. nhãn đang chọn)
 * @param {{step?: number, maxSteps?: number, pad?: number}} [opts]
 * @returns {Map<string, {dx: number, dy: number}>} độ dịch của từng nhãn cần dời
 */
export const resolveLabelOverlaps = (labels, opts = {}) => {
  const { step = 14, maxSteps = 6, pad = 2 } = opts;
  const result = new Map();
  const placed = [];
  const order = labels
    .map((l, i) => ({ l, i }))
    .sort((a, b) => (a.l.priority ?? 0) - (b.l.priority ?? 0) || a.i - b.i);

  order.forEach(({ l }) => {
    const rectAt = (dx, dy) => labelRect({ ...l, x: l.x + dx, y: l.y + dy });
    const free = (dx, dy) => {
      const r = rectAt(dx, dy);
      return placed.every(p => !rectsOverlap(r, p, pad));
    };
    let chosen = { dx: 0, dy: 0 };
    if (!free(0, 0)) {
      // Thử dọc theo đường rồi vuông góc, xa dần
      const angle = l.angle ?? 0;
      const t = { x: Math.cos(angle), y: Math.sin(angle) };
      const n = { x: -Math.sin(angle), y: Math.cos(angle) };
      let found = false;
      for (let k = 1; k <= maxSteps && !found; k += 1) {
        const d = k * step;
        const candidates = [
          [t.x * d, t.y * d],
          [-t.x * d, -t.y * d],
          [n.x * d, n.y * d],
          [-n.x * d, -n.y * d],
        ];
        for (const [dx, dy] of candidates) {
          if (free(dx, dy)) {
            chosen = { dx, dy };
            found = true;
            break;
          }
        }
      }
    }
    placed.push(rectAt(chosen.dx, chosen.dy));
    if (chosen.dx !== 0 || chosen.dy !== 0) result.set(l.id, chosen);
  });
  return result;
};

/** Nhãn có nên hiện không: ẩn khi đoạn DIM quá ngắn trên màn hình. */
export const shouldShowLabel = lengthPx => lengthPx >= MIN_LABEL_LENGTH_PX;

/** Ước lượng kích thước nhãn (px) từ chuỗi và cỡ chữ (font sans in đậm). */
export const estimateLabelSize = (text, fontSize) => ({
  w: String(text).length * fontSize * 0.62 + 6,
  h: fontSize + 4,
});
